'use client';

import type { ColumnDef } from '@benzinga/table';
import dayjs from 'dayjs';
import { FiArrowDownRight, FiArrowUpRight } from 'react-icons/fi';

import type { TopExecRow } from '@benzinga/insider-trades-manager';
import { TickerCell } from './index';
import { formatTradeSize, formatTitle } from '../formatters';
import { formatPercentage } from '@benzinga/utils';

const DELTA_OWN_TOOLTIP = 'Percentage change of owned shares for latest filing';

function interpolateColor(startHex, endHex, steps) {
  const hexToRgb = hex => {
    const bigint = parseInt(hex.replace('#', ''), 16);
    return [(bigint >> 16) & 255, (bigint >> 8) & 255, bigint & 255];
  };

  const rgbToHex = (r, g, b) => '#' + [r, g, b].map(x => Math.round(x).toString(16).padStart(2, '0')).join('');

  const start = hexToRgb(startHex);
  const end = hexToRgb(endHex);

  const step = start.map((s, i) => (end[i] - s) / (steps - 1));

  return Array.from({ length: steps }, (_, i) => {
    const rgb = start.map((s, j) => s + step[j] * i);
    return rgbToHex(rgb[0], rgb[1], rgb[2]);
  });
}

/** Precomputed gradient for Position rank column (static inputs). */
const POSITION_RANK_COLORS = interpolateColor('#3f83f8', '#283d59', 100);

export function getTopExecsColumnDefs(): ColumnDef<TopExecRow>[] {
  return [
    {
      field: 'exec_name',
      headerName: 'Position',
      width: 95,
      // make in based on row index + 1
      cellRenderer: ({ index }: { index: number }) => {
        return (
          <span
            className="text-center text-lg font-bold flex items-center justify-center w-full"
            style={{ color: POSITION_RANK_COLORS[index] }}
          >
            {index + 1}
          </span>
        );
      },
    },
    {
      field: 'exec_name',
      headerName: 'Exec Name',
      width: 180,
      cellRenderer: (params: { data: TopExecRow | null }) => {
        const row = params.data;
        if (!row) return null;
        const name = row.exec_name ?? row.insider_names ?? '—';
        const cik = row.exec_cik ?? row.insider_cik;
        const slug = name
          .replace(/\s+/g, '-')
          .toLowerCase()
          .replace(/[^a-z0-9-]/g, '');
        if (!cik) return <span className="main-text">{name}</span>;
        const extraInsiders = row?.insider_names?.replace(name, '') ?? '';
        // Plain <a> instead of next/link to avoid SSR prefetching causing stale data on the insider profile page.
        return (
          <a
            href={`/sec/insider-trades/${encodeURIComponent(cik ?? '')}/${encodeURIComponent(slug ?? '')}`}
            className="ticker-link group"
          >
            <span className="main-text">{name}</span>
            {extraInsiders && <span className="sub-text">+{extraInsiders.replace(', ', '')}</span>}
          </a>
        );
      },
    },
    {
      field: 'insider_titles_unique',
      headerName: 'Title',
      width: 140,
      valueFormatter: ({ value }) => formatTitle(value),
      cellRenderer: (params: { value: string }) => (
        <span className="main-text capitalize">{formatTitle(params.value)}</span>
      ),
    },
    {
      field: 'company_ticker',
      headerName: 'Company (Ticker)',
      width: 160,
      cellRenderer: (params: { data: TopExecRow | null }) => {
        const row = params.data;
        if (!row) return null;
        return (
          <TickerCell cik={row.company_cik ?? ''} ticker={row.company_ticker ?? ''} name={row.company_name ?? ''} />
        );
      },
    },
    {
      field: 'traded_value',
      headerName: 'Trade, $',
      headerTooltip: 'The total dollar value of the shares traded',
      width: 115,
      cellRenderer: (params: { value: number | undefined; data: TopExecRow | null }) => {
        const value = params.value;
        if (value == null) return '—';
        const isPositive = value >= 0;
        return (
          <span className={`stat-value ${isPositive ? 'positive' : 'negative'}`}>
            <span className={`mr-1 rounded-sm bg-opacity-10 ${isPositive ? 'bg-green-500' : 'bg-red-500'}`} aria-hidden>
              {isPositive ? (
                <FiArrowUpRight className="w-4 h-4 fill-green-500" size={16} />
              ) : (
                <FiArrowDownRight className="w-4 h-4 fill-red-500" size={16} />
              )}
            </span>
            {formatTradeSize(Math.abs(value))}
          </span>
        );
      },
    },
    {
      field: 'trades_count',
      headerName: 'Trades',
      width: 90,
      valueFormatter: ({ value }) => (value != null ? String(value) : '—'),
      cellRenderer: (params: { value: number | undefined }) => (
        <span className="text-center main-text px-2">{params.value != null ? String(params.value) : '—'}</span>
      ),
    },
    {
      field: 'last_trade_date',
      headerName: 'Last Trade',
      width: 110,
      headerTooltip: 'The date of the last trade filed',
      valueFormatter: ({ value }) => (value ? String(value) : '—'),
      cellRenderer: (params: { value: string }) => (
        <span className="text-xs font-bold">{params.value ? dayjs(params.value).format('MMM DD, YYYY') : '—'}</span>
      ),
    },
    {
      field: 'traded_percentage',
      headerName: 'Last Δown',
      width: 95,
      headerTooltip: DELTA_OWN_TOOLTIP,
      cellRenderer: (params: { value: number | undefined; data: TopExecRow | null }) => {
        const value = params.value;
        if (value == null) return '—';
        const isPositive = value >= 0;
        const isNewPosition = value == 9999;
        if (isNewPosition) {
          return <span className="stat-value text-2xl font-bold text-center px-2">∞</span>;
        }
        return (
          <span className={`stat-value ${isPositive ? 'positive' : 'negative'}`}>
            {isPositive ? '+' : ''}
            {formatPercentage(value as number)}%
          </span>
        );
      },
    },
    {
      field: 'last_filing_url',
      headerTooltip: 'View the latest SEC filing for these trades',
      headerName: 'SEC Filing',
      width: 90,
      cellRenderer: ({ value }: { value: string }) => {
        return (
          <a
            href={value}
            className="text-xs underline text-bzblue-800 hover:text-bzblue-700"
            aria-label="View Filing"
            target="_blank"
            rel="noopener noreferrer"
          >
            View
          </a>
        );
      },
    },
  ];
}
